/**
 * Which visits the payroll export will pay, so totals on admin screens match the export.
 * Mirrors planExport in src/worker/dal/exports.ts: a visit is paid only when it is closed,
 * has a clock-out, and both punches are ok, flagged or approved.
 */
import type { PunchResult, Visit } from "../../shared/types";

const PAYABLE_RESULTS: ReadonlySet<PunchResult> = new Set(["ok", "flagged", "approved"]);

export const isPending = (v: Visit) => v.in.result === "pending" || v.out?.result === "pending";

/** The clock-out was rejected, so its time doesn't count (the visit needs a finish time). */
export const outRejected = (v: Visit) => v.out?.result === "rejected";

/** True when the payroll export would include this visit. */
export function isPayable(v: Visit): boolean {
  return (
    v.status === "closed" &&
    v.out != null &&
    PAYABLE_RESULTS.has(v.in.result) &&
    PAYABLE_RESULTS.has(v.out.result)
  );
}

/** Rejected visits (or a rejected clock-in) never count; everything else may count later. */
export const isRejected = (v: Visit) => v.status === "rejected" || v.in.result === "rejected";

/**
 * Minutes to show in a "Worked" column: none while open, or when the clock-out was rejected
 * (its time isn't accepted, so out minus in is not time worked).
 */
export const shownMinutes = (v: Visit): number | null => (outRejected(v) ? null : v.minutes);

export interface VisitTotals {
  /** Visits the export would pay, and their minutes. */
  payableVisits: number;
  payableMinutes: number;
  /** Visits that can't be paid yet: waiting for approval, open, or missing a clock-out. */
  notPayableVisits: number;
  /** Recorded minutes of those visits that have an accepted or pending clock-out. */
  notPayableMinutes: number;
}

export function visitTotals(visits: Visit[]): VisitTotals {
  const t: VisitTotals = { payableVisits: 0, payableMinutes: 0, notPayableVisits: 0, notPayableMinutes: 0 };
  for (const v of visits) {
    if (isRejected(v)) continue;
    if (isPayable(v)) {
      t.payableVisits++;
      t.payableMinutes += v.minutes ?? 0;
    } else {
      t.notPayableVisits++;
      t.notPayableMinutes += shownMinutes(v) ?? 0;
    }
  }
  return t;
}

/** Why a visit isn't payable yet, for a short note; null when it is payable or rejected. */
export function notPayableReason(v: Visit): string | null {
  if (isRejected(v) || isPayable(v)) return null;
  if (isPending(v)) return "Waiting for approval";
  if (outRejected(v)) return "Clock-out rejected: needs a finish time";
  if (v.status === "missing_clock_out") return "Missing clock-out";
  if (v.status === "open" || !v.out) return "Still clocked in";
  return "Not payable yet";
}
